/**
 * Wireless Scanner Service for ERP Frontend
 *
 * This service enables wireless barcode scanning through a Zebra scanner
 * connected to a Star Printer via network communication. It uses keyboard
 * event listening to capture scanned data (HID mode).
 */

class WirelessScannerService {
    constructor() {
        this.isInitialized = false;
        this.isActive = false;
        this.isConnected = false;
        this.lastScanData = '';
        this.connectionStatus = 'Disconnected';
        this.scanHistory = [];
        this.statusLog = [];

        // Configuration
        this.printerIP = '10.0.0.201'; // Star Printer IP
        this.scanTimeout = 100; // Milliseconds between keystrokes to consider a scan
        this.minScanLength = 3; // Minimum characters for a valid scan

        // Event listeners and handlers
        this.scanListeners = [];
        this.statusListeners = [];
        this.keyBuffer = '';
        this.lastKeyTime = 0;
        this.scanTimer = null;

        // Keyboard event handler
        this.keydownHandler = this.handleKeydown.bind(this);

        // Scanner state management
        this.preferredScannerType = 'wireless'; // 'wireless', 'usb', 'auto'
        this.enableAutoFallback = true;

        console.log('WirelessScannerService created');
    }

    /**
     * Initialize the scanner service
     */
    async initialize() {
        if (this.isInitialized) return;

        try {
            console.log('Initializing wireless scanner service...');
            this.updateConnectionStatus('Initializing scanner...');

            // Check if printer is reachable
            await this.checkPrinterConnection();

            // Set up keyboard listener for HID mode scanning
            this.setupKeyboardListener();

            this.isInitialized = true;
            this.isConnected = true;
            this.updateConnectionStatus('Connected - Ready to scan');

            console.log('Wireless scanner service initialized successfully');
        } catch (error) {
            console.error('Failed to initialize scanner service:', error);
            this.updateConnectionStatus(`Failed to initialize: ${error.message}`);
            throw error;
        }
    }

    /**
     * Check if the printer is reachable on the network
     */
    async checkPrinterConnection() {
        try {
            // Simple connectivity check - in a real implementation, you might
            // ping the printer or make a simple HTTP request to check status
            console.log(`Checking printer connection at ${this.printerIP}...`);

            // For now, we'll assume the printer is available
            // In a production environment, you could implement actual network checking
            await new Promise(resolve => setTimeout(resolve, 500));

            console.log('Printer connection verified');
            return true;
        } catch (error) {
            console.error('Printer connection check failed:', error);
            throw new Error('Printer not reachable');
        }
    }

    /**
     * Set up keyboard event listener for HID mode scanning
     */
    setupKeyboardListener() {
        console.log('Setting up keyboard listener for scanner input...');
        document.addEventListener('keydown', this.keydownHandler, true);
    }

    /**
     * Remove keyboard event listener
     */
    removeKeyboardListener() {
        console.log('Removing keyboard listener...');
        document.removeEventListener('keydown', this.keydownHandler, true);
    }

    /**
     * Handle keydown events to detect scanner input
     */
    handleKeydown(event) {
        if (!this.isActive) return;

        const target = event.target;
        const targetTag = target && target.tagName ? target.tagName.toLowerCase() : '';
        const isEditableTarget = target && (
            target.isContentEditable ||
            ['input', 'textarea', 'select'].includes(targetTag)
        );

        if (isEditableTarget) {
            this.clearBuffer();
            return;
        }

        const currentTime = Date.now();
        const timeSinceLastKey = currentTime - this.lastKeyTime;

        if (!this.keyBuffer.length && event.key.length === 1) {
            this.keyBuffer = event.key;
            this.lastKeyTime = currentTime;
            this.scanTimer = setTimeout(() => this.clearBuffer(), this.scanTimeout);
            return;
        }

        // Check if this is likely scanner input (rapid keystrokes)
        // Scanner typically sends data much faster than human typing
        const isLikelyScannerInput = this.keyBuffer.length > 0 && timeSinceLastKey < this.scanTimeout;

        if (isLikelyScannerInput) {
            // Prevent default behavior for scanner input
            event.preventDefault();
            event.stopPropagation();

            // Handle different key types
            if (event.key === 'Enter' || event.key === 'Tab') {
                // End of scan - process the buffer
                this.processScanBuffer();
            } else if (event.key.length === 1) {
                // Regular character - add to buffer
                this.keyBuffer += event.key;
                this.lastKeyTime = currentTime;

                // Reset the scan timer
                if (this.scanTimer) {
                    clearTimeout(this.scanTimer);
                }

                // Set timer to process buffer if no more keys come
                this.scanTimer = setTimeout(() => {
                    if (this.keyBuffer.length >= this.minScanLength) {
                        this.processScanBuffer();
                    } else {
                        this.clearBuffer();
                    }
                }, this.scanTimeout);
            }
        } else {
            // Likely human typing - clear buffer
            this.clearBuffer();
        }
    }

    /**
     * Process the accumulated scan buffer
     */
    processScanBuffer() {
        if (this.keyBuffer.length >= this.minScanLength) {
            const scannedData = this.keyBuffer.trim();

            this.handleScanData(scannedData);
        }

        this.clearBuffer();
    }

    /**
     * Clear the key buffer and timer
     */
    clearBuffer() {
        this.keyBuffer = '';
        this.lastKeyTime = 0;

        if (this.scanTimer) {
            clearTimeout(this.scanTimer);
            this.scanTimer = null;
        }
    }

    /**
     * Handle successful scan data
     */
    handleScanData(scanData) {
        if (!scanData || scanData.length < this.minScanLength) return;

        const cleanData = scanData.trim();

        // Store scan data
        this.lastScanData = cleanData;
        this.scanHistory.unshift(cleanData);

        // Keep only last 50 scans
        if (this.scanHistory.length > 50) {
            this.scanHistory.splice(50);
        }

        // Update status
        this.updateConnectionStatus(`Last scan: ${cleanData}`);

        // Notify listeners
        this.notifyScanListeners(cleanData);

        // Auto-reset status after delay
        setTimeout(() => {
            if (this.isActive) {
                this.updateConnectionStatus('Scanner active - Ready to scan');
            }
        }, 2000);
    }

    /**
     * Start scanning
     */
    async startScanning() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        if (this.isActive) {
            console.log('Scanner already active');
            return;
        }

        console.log('Starting wireless scanner...');
        this.isActive = true;
        this.updateConnectionStatus('Scanner active - Ready to scan');

        // Clear any existing buffer
        this.clearBuffer();
    }

    /**
     * Stop scanning
     */
    stopScanning() {
        if (!this.isActive) return;

        console.log('Stopping wireless scanner...');
        this.isActive = false;
        this.updateConnectionStatus('Scanner stopped');

        // Clear buffer
        this.clearBuffer();
    }

    /**
     * Subscribe to scan events
     */
    onScan(callback) {
        if (typeof callback === 'function') {
            this.scanListeners.push(callback);
        }
    }

    /**
     * Unsubscribe from scan events
     */
    offScan(callback) {
        const index = this.scanListeners.indexOf(callback);
        if (index > -1) {
            this.scanListeners.splice(index, 1);
        }
    }

    /**
     * Subscribe to status events
     */
    onStatusChange(callback) {
        if (typeof callback === 'function') {
            this.statusListeners.push(callback);
        }
    }

    /**
     * Unsubscribe from status events
     */
    offStatusChange(callback) {
        const index = this.statusListeners.indexOf(callback);
        if (index > -1) {
            this.statusListeners.splice(index, 1);
        }
    }

    /**
     * Notify scan listeners
     */
    notifyScanListeners(scanData) {
        this.scanListeners.forEach(callback => {
            try {
                callback(scanData);
            } catch (error) {
                console.error('Error in scan listener:', error);
            }
        });
    }

    /**
     * Notify status listeners
     */
    notifyStatusListeners(status) {
        this.statusListeners.forEach(callback => {
            try {
                callback(status);
            } catch (error) {
                console.error('Error in status listener:', error);
            }
        });
    }

    /**
     * Update connection status
     */
    updateConnectionStatus(status) {
        this.connectionStatus = status;
        this.statusLog.unshift(`${new Date().toISOString()}: ${status}`);

        // Keep only last 20 status entries
        if (this.statusLog.length > 20) {
            this.statusLog.splice(20);
        }

        console.log(`Scanner status: ${status}`);
        this.notifyStatusListeners(status);
    }

    /**
     * Test scan functionality
     */
    testScan(value = 'TEST_BARCODE_' + Date.now()) {
        console.log('Simulating scan:', value);
        this.handleScanData(value);
    }

    /**
     * Clear scan history
     */
    clearHistory() {
        this.scanHistory = [];
        console.log('Scan history cleared');
    }

    /**
     * Clear status log
     */
    clearStatusLog() {
        this.statusLog = [];
        console.log('Status log cleared');
    }

    /**
     * Get scanner status information
     */
    getStatusInfo() {
        return {
            initialized: this.isInitialized,
            connected: this.isConnected,
            active: this.isActive,
            status: this.connectionStatus,
            lastScan: this.lastScanData,
            scanHistory: [...this.scanHistory],
            statusLog: [...this.statusLog],
            printerIP: this.printerIP,
            scanTimeout: this.scanTimeout,
            minScanLength: this.minScanLength
        };
    }

    /**
     * Get scan statistics
     */
    getScanStats() {
        return {
            serviceReady: this.isConnected,
            scannerActive: this.isActive,
            lastScanTime: this.lastScanData ? new Date().toISOString() : null,
            totalScans: this.scanHistory.length,
            printerIP: this.printerIP,
            initialized: this.isInitialized
        };
    }

    /**
     * Update scanner configuration
     */
    updateConfiguration(config) {
        if (config.printerIP) this.printerIP = config.printerIP;
        if (config.scanTimeout) this.scanTimeout = config.scanTimeout;
        if (config.minScanLength) this.minScanLength = config.minScanLength;

        console.log('Scanner configuration updated:', config);
    }

    /**
     * Reconnect to scanner
     */
    async reconnect() {
        console.log('Reconnecting to scanner...');

        // Stop current scanning
        this.stopScanning();

        // Reset state
        this.isInitialized = false;
        this.isConnected = false;

        // Wait a moment
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Re-initialize
        await this.initialize();

        // Start scanning if it was active before
        if (this.preferredScannerType === 'wireless' || this.preferredScannerType === 'auto') {
            await this.startScanning();
        }
    }

    /**
     * Cleanup resources
     */
    cleanup() {
        console.log('Cleaning up wireless scanner service...');

        this.stopScanning();
        this.removeKeyboardListener();

        // Clear all listeners
        this.scanListeners = [];
        this.statusListeners = [];

        // Clear timers
        this.clearBuffer();

        this.isInitialized = false;
        this.isConnected = false;
        this.updateConnectionStatus('Disconnected');
    }
}

// Create singleton instance
const wirelessScannerService = new WirelessScannerService();

export default wirelessScannerService;
